import React, { createContext, useContext, useEffect, useState } from "react"

/**
 * Cart state.
 *
 * The cart only ever stores a minimal shape in React state + localStorage:
 *
 *   { id: "uuid-here", quantity: 1 }
 *
 * It never stores titles, prices or images — those always come from the
 * Markdown/GraphQL data so there is a single source of truth for product info.
 */

const CART_STORAGE_KEY = "gatsby_minimal_shop_cart"

const CartContext = createContext(undefined)

/**
 * Read + sanitize the cart from localStorage. SSR-safe: returns [] on the
 * server because `window` is undefined there.
 */
const readStoredCart = () => {
  if (typeof window === "undefined") return []
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(
        (item) =>
          item &&
          typeof item.id === "string" &&
          Number.isFinite(item.quantity) &&
          item.quantity > 0
      )
      .map((item) => ({
        id: item.id,
        quantity: Math.max(1, Math.floor(item.quantity)),
      }))
  } catch {
    // Corrupt JSON or storage disabled — start with an empty cart.
    return []
  }
}

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([])
  // We only persist AFTER the first client-side hydration, so SSR markup and
  // the initial client render match (both start from an empty cart) and we
  // never clobber stored data before it has been read.
  const [hydrated, setHydrated] = useState(false)

  // Load from localStorage once, on the client only.
  useEffect(() => {
    setItems(readStoredCart())
    setHydrated(true)
  }, [])

  // Persist on every change, but never during SSR and never before hydration.
  useEffect(() => {
    if (!hydrated) return
    if (typeof window === "undefined") return
    try {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items))
    } catch {
      // Storage might be unavailable (private mode, quota). Fail silently.
    }
  }, [items, hydrated])

  const addItem = (productId) => {
    setItems((prev) => {
      const existing = prev.find((item) => item.id === productId)
      if (existing) {
        return prev.map((item) =>
          item.id === productId
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      }
      return [...prev, { id: productId, quantity: 1 }]
    })
  }

  const removeItem = (productId) => {
    setItems((prev) => prev.filter((item) => item.id !== productId))
  }

  const increaseItem = (productId) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === productId ? { ...item, quantity: item.quantity + 1 } : item
      )
    )
  }

  const decreaseItem = (productId) => {
    setItems((prev) =>
      prev
        .map((item) =>
          item.id === productId
            ? { ...item, quantity: item.quantity - 1 }
            : item
        )
        // Dropping to 0 removes the line entirely.
        .filter((item) => item.quantity > 0)
    )
  }

  const clearCart = () => setItems([])

  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0)

  const value = {
    items,
    totalQuantity,
    addItem,
    removeItem,
    increaseItem,
    decreaseItem,
    clearCart,
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

/**
 * Access the cart from any component. Throws if used outside the provider,
 * which only happens if the gatsby-browser/gatsby-ssr wiring is missing.
 */
export const useCart = () => {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider")
  }
  return context
}
